//method signature for setTimeout callback function.
type TimeoutCallbackFunc = () => void;

//Hash of timeoutId to callback function
interface ITimeoutCallbackHash {
  [timeoutId: string]: TimeoutCallbackFunc;
}

/**
 * setTimeout and setInterval can be slowed down by heavy processing, thus making them inaccurate mechanisms for scheduling in some scenarios.
 * We can provide better accuracy by having setTimeout, etc execute in its own thread via the use of a web worker.
 * https://medium.com/@abhi9bakshi/why-javascript-timer-is-unreliable-and-how-can-you-fix-it-9ff5e6d34ee0
 * https://medium.com/teads-engineering/the-most-accurate-way-to-schedule-a-function-in-a-web-browser-eadcd164da12
 */
class AccurateTimingWorker{
  //web worker that executes setTimeout, etc and messages back and forth with this class.
  worker: Worker;
  //keep track of which callback function to execute for a given timeout id.
  timeoutCallbackHash: ITimeoutCallbackHash = {};
  constructor(){
    this.worker = createWorkerFromFunction(timingWorkerFunction);
    this.worker.onmessage = (e) => this.handleMessageFromWorker(e);
  }

  /**
   * Provide a similar interface to setTimeout, but rather than return an id, return a function that clears the timeout.
   * @param callback - function to execute after callbackMs has passed.
   * @param callbackMs - ms until callback should be executed.
   */
  setTimeout(callback: TimeoutCallbackFunc, callbackMs: number){
    //generate a new timeout id
    const timeoutId = Date.now() + '' + Math.random();
    //tell the worker to schedule the timeout
    this.worker.postMessage({method: workerMethods.setTimeout, callbackMs, timeoutId,});
    //register the timeout id and callback in our hash, so we can execute the timeout when the worker calls us back with the timeout id.
    this.timeoutCallbackHash[timeoutId] = callback;
    //return a function that cancels the timeout, if needed by the caller.
    return (forceCallToCallback = false) => this.clearTimer(timeoutId, forceCallToCallback);
  }

  /**
   * The worker calls this function (through messaging) when a timeout has transpired.
   * This function uses the timeoutId to find and execute the registered callback func.
   * @param timeoutId - timeout id sent to the worker when setTimeout is called.
   */
  handleSetTimeoutCallback(timeoutId: string){
    const callback = this.timeoutCallbackHash[timeoutId];
    delete this.timeoutCallbackHash[timeoutId];
    if(callback){
      callback();
    }
  }

  /**
   * Handle messages from our worker thread.
   * @param e - message event from worker
   */
  handleMessageFromWorker(e: MessageEvent){
    const method = e.data.method;
    switch(method){
      case `setTimeoutCallback`:
        this.handleSetTimeoutCallback(e.data.timeoutId);
        break;
    }
  }

  /**
   * Cancel previously scheduled setTimeout func.
   * @param timeoutId - id generated by setTimeout
   * @param forceCallToCallback
   */
  clearTimer(timeoutId: string, forceCallToCallback = false){
    if(forceCallToCallback){
      const callback = this.timeoutCallbackHash[timeoutId];
      if(callback){
        delete this.timeoutCallbackHash[timeoutId];
        callback();
      }
    }
    this.worker.postMessage({method: workerMethods.clearTimeout, timeoutId});
    delete this.timeoutCallbackHash[timeoutId];
  }
}

//functions the worker thread provides.
const workerMethods = {
  setTimeout: `setTimeout`,
  clearTimeout: `clearTimeout`,
};

//hash to map external timeoutId (ie. the one provided by AccurateTimingWorker.setTimeout) to internal timeoutId generated by call to setTimeout.
interface ITimeoutIdHash {
  [externalId: string]: number;
}

/**
 * Work threads are represented by a single function.
 */
function timingWorkerFunction(){
  //@ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-this-alias
  const self = this;
  const timeoutIdHash: ITimeoutIdHash = {};

  /**
   * Handle messages from parent.  Evaluate the method name and call appropriate function.
   * @param e - worker message event with data sent from parent.
   */
  self.onmessage = function(e: MessageEvent) {
    const method = e.data.method;
    switch (method) {
      case `setTimeout`:
        handleSetTimeout(e.data.callbackMs, e.data.timeoutId,);
        break;
      case `clearTimeout`:
        handleClearTimeout(e.data.timeoutId);
        break;
      default:
        break;
    }
  };

  /**
   * executes a setTimeout, posting a message to the parent with the passed in timeoutId.
   * @param callbackMs - when the parent should be called back with the setTimeoutCallback message
   * @param timeoutId - timeoutId generated by the parent.
   */
  function handleSetTimeout(callbackMs: number, timeoutId: string,){
    const internalSetTimeoutId = setTimeout(function(){
      self.postMessage({method: `setTimeoutCallback`, callbackMs, timeoutId,});
      delete timeoutIdHash[timeoutId];
    }, callbackMs);
    //@ts-ignore setTimeout is assumed to be node js, which returns Timeout, rather than number. Normal fix is to use window.setTimeout, but window is not defined here.
    timeoutIdHash[timeoutId] = internalSetTimeoutId;
  }

  /**
   * Cancels a previously scheduled timeout
   * @param timeoutId - timeoutId generated by the parent.
   */
  function handleClearTimeout(timeoutId: string){
    const internalId = timeoutIdHash[timeoutId];
    clearTimeout(internalId);
    delete timeoutIdHash[timeoutId];
  }
}

/**
 * Workers typically need to be loaded as a separate script file, which is inconvenient.
 * We can get around this by stringifying the worker function and creating an object url.
 * @param func
 */
function createWorkerFromFunction(func: Function){
  const dataObj = '(' + func + ')();';
  const blob = new Blob([dataObj]);
  const blobURL = URL.createObjectURL(blob);
  return new Worker(blobURL);
}

const worker = new AccurateTimingWorker();
export default worker;